/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/guild/GuildBadge.module.css';
import {DiscoverableBadgeIcon} from '@app/components/icons/DiscoverableBadgeIcon';
import {PartneredBadgeIcon} from '@app/components/icons/PartneredBadgeIcon';
import {VerifiedBadgeIcon} from '@app/components/icons/VerifiedBadgeIcon';
import {Tooltip} from '@app/components/uikit/tooltip/Tooltip';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {useLingui} from '@lingui/react/macro';

interface GuildBadgeProps {
	readonly features: ReadonlySet<string> | ReadonlyArray<string>;
	readonly variant?: 'default' | 'large' | 'banner';
	readonly tooltipPosition?: 'top' | 'bottom';
	readonly showTooltip?: boolean;
	readonly forceDarkTheme?: boolean;
	readonly onLightSurface?: boolean;
}

function hasFeature(features: ReadonlySet<string> | ReadonlyArray<string>, feature: string): boolean {
	if (Array.isArray(features)) {
		return features.includes(feature);
	}
	return (features as ReadonlySet<string>).has(feature);
}

export function GuildBadge({
	features,
	variant = 'default',
	tooltipPosition = 'top',
	showTooltip = true,
	forceDarkTheme = false,
	onLightSurface = false,
}: GuildBadgeProps) {
	const {t} = useLingui();

	const isVerified = hasFeature(features, GuildFeatures.VERIFIED);
	const isPartnered = hasFeature(features, GuildFeatures.PARTNERED);
	const isDiscoverable = hasFeature(features, GuildFeatures.DISCOVERABLE);

	if (!isVerified && !isPartnered && !isDiscoverable) {
		return null;
	}

	const badgeSize = variant === 'large' ? 24 : 20;
	const badgeClassName =
		variant === 'banner'
			? forceDarkTheme
				? styles.badgeBannerDark
				: styles.badgeBanner
			: onLightSurface
				? styles.badgeOnLightSurface
				: styles.badge;

	let tooltipText: string;
	let icon: React.JSX.Element;

	if (isPartnered) {
		tooltipText = isVerified ? t`Verified & Partnered Community` : t`Partnered Community`;
		icon = <PartneredBadgeIcon size={badgeSize} className={badgeClassName} />;
	} else if (isVerified) {
		tooltipText = t`Verified Community`;
		icon = <VerifiedBadgeIcon size={badgeSize} className={badgeClassName} />;
	} else {
		tooltipText = t`Discoverable Community`;
		icon = <DiscoverableBadgeIcon size={badgeSize} className={badgeClassName} />;
	}

	if (!showTooltip) {
		return icon;
	}

	return (
		<Tooltip text={tooltipText} position={tooltipPosition}>
			<span className={styles.badgeWrapper}>{icon}</span>
		</Tooltip>
	);
}
